import { apiClient } from "@/lib/api/api-client";
import { useClientTranslations } from "@/lib/translate";
import { AppError } from "@/lib/utils/errors";
import { routes } from "@/lib/utils/routes";
import { AuthService } from "@/modules/services/auth/authService";
import { BusinessService } from "@/modules/services/business/businessService";
import { CompanySize } from "@/modules/shared/types/enum";
import { useBusinessInfoStore } from "@/modules/store/businessInfoStore";
import { useAuthStore } from "@/modules/store/authStore";
import { useRegistInfoStore } from "@/modules/store/registInfoStore";
import { useMetadataStore } from "@/modules/store/metadataStore";
import { RegisterData } from "@/modules/types/auth";
import { BusinessCreateInfo } from "@/modules/types/business";
import {
  validateEmail,
  validatePassword,
  validatePhone,
} from "@/modules/utils/auth/validators";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/modules/hooks/auth/useAuth";

export const useSignup = () => {
  const { t } = useClientTranslations("signup-page");
  const [firstTime, setFirstTime] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [readTerms, setReadTerms] = useState(false);
  const [errorEmail, setErrorEmail] = useState("");
  const [errorPassword, setErrorPassword] = useState("");
  const [errorRepeatPassword, setErrorRepeatPassword] = useState("");
  const [errorTerms, setErrorTerms] = useState("");
  const [errorSubmit, setErrorSubmit] = useState("");
  const [loading, setLoading] = useState(false);
  const { step, done, setStep } = useRegistInfoStore();
  const { businessInfo } = useBusinessInfoStore();
  const { setToken, setRefreshToken, setExpired } = useAuthStore();
  const { login } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (firstTime) {
      if (businessInfo && businessInfo.id && businessInfo.id.length > 0) {
        router.push(routes.home);
        return;
      }
      if (done) {
        router.push(routes.home);
        return;
      }
      if (step == 1) {
        router.push(routes.registerInfo);
        return;
      }
      setFirstTime(false);
    }
  }, []);

  useEffect(() => {
    if (validateEmail(email)) {
      setErrorEmail("");
    } else {
      if (firstTime) {
        return;
      }
      setErrorEmail(t("signup.invalidEmail"));
    }
  }, [email]);

  useEffect(() => {
    if (validatePassword(password)) {
      setErrorPassword("");
    } else {
      if (firstTime) {
        return;
      }
      setErrorPassword(t("signup.invalidPassword"));
    }
  }, [password]);

  useEffect(() => {
    if (password === repeatPassword) {
      setErrorRepeatPassword("");
    } else {
      if (firstTime) {
        return;
      }
      setErrorRepeatPassword(t("signup.passwordsNotMatch"));
    }
  }, [password, repeatPassword]);

  useEffect(() => {
    if (readTerms) {
      setErrorTerms("");
    }
  }, [readTerms]);

  const signup = async () => {
    setLoading(true);
    if (!readTerms) {
      setErrorTerms(t("signup.termsRequired"));
      setLoading(false);
      return;
    }
    if (
      !validateEmail(email) ||
      !validatePassword(password) ||
      password !== repeatPassword
    ) {
      setLoading(false);
      return;
    }
    try {
      const svc = new AuthService();
      const registerData: RegisterData = {
        email: email,
        password: password,
        firstName: "",
        lastName: "",
      };
      const resp = await svc.register(registerData);
      apiClient.setToken(resp.token);
      setToken({
        token: resp.token,
        expired: resp.expiredAt,
      });
      setRefreshToken({
        token: resp.refreshToken,
        expired: resp.expiredAt + 3600,
      });
      setExpired(resp.expiredAt);
      setStep(1);
      router.push(routes.registerInfo);
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      setErrorSubmit(appErr.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        }).then(res => res.json());

        await login(
          userInfo.email,
          "",
          true,
          "google",
          userInfo.sub,
          userInfo.name
        );
        
        // After successful login, redirect to registerInfo step to fill business info
        setStep(1);
        router.push(routes.registerInfo);
      } catch (error) {
        const appErr = new AppError(error);
        appErr.logError("useSignup.handleGoogleLogin");
        setErrorSubmit(t("signup.error")); // Assuming signup.error or similar exists
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setErrorSubmit(t("signup.error"));
    }
  });

  const handleGoogleLogin = () => {
    if (!readTerms) {
      setErrorTerms(t("signup.termsRequired"));
      return;
    }
    setErrorTerms("");
    googleLogin();
  };

  return {
    loading,
    email,
    password,
    repeatPassword,
    readTerms,
    signup,
    setEmail,
    setPassword,
    setRepeatPassword,
    setReadTerms,
    showPassword,
    showRepeatPassword,
    setShowPassword,
    setShowRepeatPassword,
    errorEmail,
    errorPassword,
    errorRepeatPassword,
    errorSubmit,
    errorTerms,
    handleGoogleLogin,
  };
};

export const useSignupBusinessInfo = () => {
  const { t } = useClientTranslations("signup-page");
  const [loading, setLoading] = useState(false);
  const [businessCreateInfo, setBusinessCreateInfo] =
    useState<BusinessCreateInfo>({
      companyName: "",
      shortCompanyName: "",
      representativePhone: "",
      website: "",
      taxCode: "",
      companySize: CompanySize.SMALL,
      industryId: "",
    });
  const [errCompanyName, setErrCompanyName] = useState<string>();
  const [errShortCompanyName, setErrShortCompanyName] = useState<string>();
  const [errRepresentativePhone, setErrRepresentativePhone] =
    useState<string>();
  const [errWebsite, setErrWebsite] = useState<string>();
  const [errTaxCode, setErrTaxCode] = useState<string>();
  const [errCompanySize, setErrCompanySize] = useState<string>();
  const [errIndustryId, setErrIndustryId] = useState<string>();
  const [errorSubmit, setErrorSubmit] = useState<string>();
  const { step, done, setStep, setDone } = useRegistInfoStore();
  const { businessInfo, setBusinessInfo } = useBusinessInfoStore();
  const { industries } = useMetadataStore();
  const router = useRouter();

  useEffect(() => {
    if (businessInfo && businessInfo.id && businessInfo.id.length > 0) {
      router.push(routes.home);
      return;
    }
    if (done) {
      router.push(routes.home);
      return;
    }
    if (step == 0) {
      router.push(routes.register);
      return;
    }
  }, []);

  useEffect(() => {
    if (industries.length > 0 && !businessCreateInfo.industryId) {
      setIndustryId(industries[0].id);
    }
  }, [industries, businessCreateInfo.industryId]);

  const setCompanyName = (name: string) => {
    setBusinessCreateInfo((prev) => ({ ...prev, companyName: name }));
    validateCompanyName(name);
  };

  const setShortCompanyName = (name: string) => {
    setBusinessCreateInfo((prev) => ({ ...prev, shortCompanyName: name }));
    validateShortCompanyName(name);
  };

  const setRepresentativePhone = (phone: string) => {
    setBusinessCreateInfo((prev) => ({ ...prev, representativePhone: phone }));
    validateRepresentativePhone(phone);
  };

  const setWebsite = (url: string) => {
    setBusinessCreateInfo((prev) => ({ ...prev, website: url }));
    validateWebsite(url);
  };

  const setTaxCode = (code: string) => {
    setBusinessCreateInfo((prev) => ({ ...prev, taxCode: code }));
    validateTaxCode(code);
  };

  const setCompanySize = (size: CompanySize) => {
    setBusinessCreateInfo((prev) => ({ ...prev, companySize: size }));
    validateCompanySize(size);
  };

  const setIndustryId = (id: string) => {
    setBusinessCreateInfo((prev) => ({ ...prev, industryId: id }));
    validateIndustryId(id);
  };

  const validateCompanyName = (name: string) => {
    if (!name || name.length < 2) {
      setErrCompanyName(t("business.invalidCompanyName"));
      return false;
    } else {
      setErrCompanyName(undefined);
      return true;
    }
  };

  const validateShortCompanyName = (name: string) => {
    if (!name || name.length < 2) {
      setErrShortCompanyName(t("business.invalidCompanyNameShort"));
      return false;
    } else {
      setErrShortCompanyName(undefined);
      return true;
    }
  };

  const validateRepresentativePhone = (phone: string) => {
    if (!validatePhone(phone)) {
      setErrRepresentativePhone(t("business.invalidCompanyPhone"));
      return false;
    } else {
      setErrRepresentativePhone(undefined);
      return true;
    }
  };

  const validateWebsite = (url: string) => {
    const re =
      /(https?:\/\/)?([\w\-])+\.{1}([a-zA-Z]{2,63})([\/\w-]*)*\/?\??([^#\n\r]*)?#?([^\n\r]*)/g;
    if (!re.test(url)) {
      setErrWebsite(t("business.invalidWebsite"));
      return false;
    } else {
      setErrWebsite(undefined);
      return true;
    }
  };

  const validateTaxCode = (code: string) => {
    const reg = /^\d+$/;
    if (code.length < 8 || !reg.test(code)) {
      setErrTaxCode(t("business.invalidTaxCode"));
      return false;
    } else {
      setErrTaxCode(undefined);
      return true;
    }
  };

  const validateCompanySize = (size: CompanySize) => {
    if (!size) {
      setErrCompanySize(t("business.invalidCompanySize"));
      return false;
    } else {
      setErrCompanySize(undefined);
      return true;
    }
  };

  const validateIndustryId = (id: string) => {
    if (!id || id.length < 2) {
      setErrIndustryId(t("business.invalidIndustry"));
      return false;
    } else {
      setErrIndustryId(undefined);
      return true;
    }
  };

  const saveInfo = async () => {
    if (
      !validateCompanyName(businessCreateInfo.companyName) ||
      !validateShortCompanyName(businessCreateInfo.shortCompanyName) ||
      !validateRepresentativePhone(businessCreateInfo.representativePhone) ||
      !validateWebsite(businessCreateInfo.website) ||
      !validateCompanySize(businessCreateInfo.companySize) ||
      !validateIndustryId(businessCreateInfo.industryId)
    )
      return;
    setLoading(true);
    try {
      const svc = new BusinessService();
      const resp = await svc.registBusiness(businessCreateInfo);
      if (resp) {
        setBusinessInfo(resp);
        setStep(0);
        setDone(true);
        router.push(routes.home);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      setErrorSubmit(appErr.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    companyName: businessCreateInfo.companyName,
    shortCompanyName: businessCreateInfo.shortCompanyName,
    representativePhone: businessCreateInfo.representativePhone,
    website: businessCreateInfo.website,
    taxCode: businessCreateInfo.taxCode,
    companySize: businessCreateInfo.companySize,
    industryId: businessCreateInfo.industryId,
    setCompanyName,
    setShortCompanyName,
    setRepresentativePhone,
    setWebsite,
    setTaxCode,
    setCompanySize,
    setIndustryId,
    saveInfo,
    errCompanyName,
    errShortCompanyName,
    errRepresentativePhone,
    errWebsite,
    errTaxCode,
    errCompanySize,
    errIndustryId,
    errorSubmit,
  };
};
